<template>
  <b-row :class="{ 'item--no-image': !item.imageUrl }">
    <b-col md="6" lg="7">
      <h4>{{ item.name }}</h4>
      <p class="mb-6">{{ item.description }}</p>

      <b-form-group v-for="optionSet in item.optionSets" :key="optionSet.id" :label="`${optionSet.name}`"
                    class="item-details__section">
        <div class="item-details__options font-sm">
          <b-radio-group v-model="chosen[optionSet.id]" v-if="optionSet.max === 1 && optionSet.options.length > 1"
                         stacked>
            <b-row>
              <b-col lg="6" v-for="(option, index) in optionSet.options" :key="`${optionSet.id}/${option.id}`"
                   class="item-details__option" :class="{ active: chosen[optionSet.id] === option.id }">
                <b-radio :value="option.id">
                  {{ option.name }}
                  <span v-if="!optionSet.free" :data-price="optionPrice(optionSet, option) | cents">+</span>
                </b-radio>
              </b-col>
            </b-row>
          </b-radio-group>

          <b-row v-else>
            <b-col lg="6" v-for="option in optionSet.options" :key="`${optionSet.id}/${option.id}`"
                   class="item-details__option">
              <b-checkbox v-model="chosen[optionSet.id][option.id]" :class="{ active: chosen[optionSet.id][option.id] }"
                          :disabled="isDisabledOption(optionSet, option)">
                <span :data-price="optionPrice(optionSet, option) | cents">+</span>
                {{ option.name }}
              </b-checkbox>
            </b-col>
          </b-row>
        </div>
      </b-form-group>

      <div class="item-details__section">
        <b-form-group label="Notes:" description="Please Note: Meal adjustments made in comments section that are
          associated with an extra charge will not be processed by the restaurant. Please request menu modifiers by
          emailing <a href='mailto:team@forkable.com'>team@forkable.com</a>">
          <b-textarea v-model="newNotes" class="item-notes" placeholder="Your message here..." rows="3"
                      maxlength="100"/>
        </b-form-group>
      </div>
    </b-col>

    <b-col md="6" lg="5" class="item-details__sidebar">
      <img v-if="item.imageUrl" :src="item.imageUrl" :alt="item.name" class="img-fluid"/>

      <fieldset class="item-details__section mt-6" v-if="allIngredients.length > 0">
        <legend class="col-form-label">Contains</legend>
        <div class="item-details__ingredients mb-3">
          <span v-for="ingredient in allIngredients" :key="ingredient">
            <IngredientIcon :ingredient="ingredient" color="primary"/>
            {{ ingredient | capitalize }}
          </span>
        </div>
        <span class="font-xs text-muted mr-1">
          May contain other restricted ingredients
        </span>
        <IngredientsWarningPopover/>
      </fieldset>
    </b-col>
  </b-row>
</template>

<script>
import IngredientsWarningPopover from './ingredients-warning-popover'
import IngredientIcon from './ingredient-icon'
import { filterUnique, flatten } from '../../utilities/array'

const visibleIngredients = ['dairy', 'gluten', 'nuts', 'seafood', 'eggs']

const dietMap = {
  seafood_allergy: 'seafood',
  dairy_free: 'dairy',
  gluten_free: 'gluten',
  nut_allergy: 'nuts',
  soy_allergy: 'soy',
  egg_free: 'eggs',
}

export default {
  props: {
    item: { type: Object, required: true },
    ingredientsMap: { type: Array, required: true },
    selections: { type: Object },
    notes: { type: String },
  },

  data() {
    let chosen = {}
    this.item.optionSets.forEach(set => {
      if (set.max === 1 && set.options.length > 1) {
        const selected = this.selections ? this.selections[set.modifierId] : null
        chosen[set.id] = (selected || [])[0] || (set.required ? set.options[0].id : -1)
      } else {
        let options = {}
        set.options.forEach(option => {
          options[option.id] = this.selections ? (this.selections[set.modifierId] || []).includes(option.id) : false
        })
        chosen[set.id] = options
      }
    })

    const itemIngredients = this.ingredientsMap.map(map => {
      if (!this.item.ingredients) return null

      const visible = visibleIngredients.indexOf(map.category) >= 0
      if (visible && map.ingredients.find(ingredient => this.item.ingredients.includes(ingredient))) {
        return map.category
      } else {
        return null
      }
    })
      .filter(ingredient => !!ingredient)
      .sort((a, b) => visibleIngredients.indexOf(a) < visibleIngredients.indexOf(b) ? -1 : 1)

    return {
      chosen,
      totalPrice: 0,
      newNotes: this.notes,
      itemIngredients,
      allIngredients: [],
    }
  },

  computed: {
    selectionsHash() {
      let hash = {}
      let modifierId = null
      Object.entries(this.chosen).forEach(([setId, choice]) => {
        modifierId = this.item.optionSets.find(set => set.id === parseInt(setId)).modifierId
        hash[modifierId] = []

        if (typeof choice === 'object') {
          Object.entries(choice).forEach(([optionId, isChosen]) => {
            if (isChosen) hash[modifierId].push(parseInt(optionId))
          })
        } else {
          hash[modifierId].push(choice)
        }
      })
      return hash
    },

    chosenIngredients() {
      let chosen = []
      let set = null
      Object.entries(this.chosen).forEach(([setId, choice]) => {
        set = this.item.optionSets.find(set => set.id === parseInt(setId))
        let ingredients = null
        if (typeof choice === 'object') {
          Object.entries(choice).forEach(([optionId, isChosen]) => {
            if (isChosen) ingredients = set.options.find(option => option.id === parseInt(optionId)).ingredients
          })
        } else if (choice > -1) {
          ingredients = set.options.find(option => option.id === choice).ingredients
        }
        if (ingredients) chosen.push(ingredients)
      })

      return [...new Set(flatten(chosen))]
    }
  },

  watch: {
    chosen: {
      handler(newVal, oldVal) {
        this.updateIngredients()
        this.updateTotal()
      },
      deep: true,
    },
  },

  created() {
    this.updateIngredients()
  },

  methods: {
    isDisabledOption(optionSet, option) {
      if (optionSet.max) {
        const chosenCount =
          Object.entries(this.chosen[optionSet.id]).map(([_id, chosen]) => chosen).filter(b => b).length
        return chosenCount >= optionSet.max && !this.chosen[optionSet.id][option.id]
      }
    },

    optionPrice(optionSet, option) {
      return this.item.menu.legacy.priceForModifierWithOptionAndSize({
        modifier_id: optionSet.modifierId,
        option_id: option.id,
      })
    },

    updateIngredients() {
      const ingredients = flatten(this.ingredientsMap.map(ingredient => ingredient.ingredients))
      const optionIngredients = this.ingredientsMap.filter(ingredient =>
        ingredient.ingredients.find(i => this.chosenIngredients.indexOf(i) !== -1)
      )
      this.allIngredients = filterUnique(this.itemIngredients.concat(optionIngredients.map(i => i.category)))
    },

    updateTotal() {
      let total = 0
      Object.entries(this.chosen).forEach(([setId, choice]) => {
        let optionSet = this.item.optionSets.find(set => set.id === parseInt(setId))

        if (typeof choice === 'object') {
          Object.entries(choice).forEach(([optionId, isChosen]) => {
            let option = optionSet.options.find(option => option.id === parseInt(optionId))
            if (isChosen) total += this.optionPrice(optionSet, option)
          })
        } else if (choice > -1) {
          let option = optionSet.options.find(option => option.id === choice)
          total += this.optionPrice(optionSet, option)
        }
      })

      this.totalPrice = total
      this.$emit('totalChange', this.totalPrice)
    },
  },

  components: {
    IngredientIcon, IngredientsWarningPopover,
  },
}
</script>

<style lang="scss">
.item-details__section {
  legend {
    border-top: 1px solid $card-border-color;
    padding: 0;
    line-height: 3rem;
    font-weight: bold;
  }

  .item-notes ~ .form-text {
    margin-top: 2rem;
    font-size: 11px;
  }
}

.item-details__option {
  margin-bottom: 1rem;
  display: block;

  .custom-control {
    display: block;
    margin-right: 0;
  }

  .custom-control-label, .custom-control {
    display: block;
  }

  [data-price] {
    float: right;
  }

  [data-price]:after {
    content: attr(data-price);
  }

  [data-price="$0.00"] {
    color: transparent !important;
  }

  [data-price="$0.00"]:after {
    content: "0.00";
    color: $black;
  }
}

.item-details__option.active [data-price], .item-details__option.active [data-price]:after {
  color: $primary;
}

.card .item-details__sidebar { /* .card for specificity */
  @include media-breakpoint-up(md) {
    position: fixed;
    padding: 0 $grid-gutter-width / 2;
  }

  @include media-breakpoint-only(xl) {
    right: calc((100vw - #{map_get($container-max-widths, 'xl')}) / 2);
    width: map_get($container-max-widths, 'xl') * 0.4166667;
  }

  @include media-breakpoint-only(lg) {
    right: calc((100vw - #{map_get($container-max-widths, 'lg')}) / 2);
    width: map_get($container-max-widths, 'lg') * 0.4166667;
  }

  @include media-breakpoint-only(md) {
    right: calc((100vw - #{map_get($container-max-widths, 'md')}) / 2);
    width: map_get($container-max-widths, 'md') * 0.5;
  }
}

.item-details__ingredients {
  color: $primary;
  font-size: $font-size-xs;

  span {
    display: inline-block;
    padding: 0 1rem;
  }

  svg {
    display: block;
    margin: 0 auto 0.25rem;
  }
}

.card .item--no-image {
  & > div {
    width: 100%;
    max-width: none;
    right: auto;
    flex: 100%;
    position: relative;
  }

  .item-details__section {
    margin-top: map_get($spacers, 2) !important;
  }
}
</style>
